import {
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { Edge, Node, ReactFlowInstance } from '@xyflow/react';
import { getFlowchartEntrance, scheduleFlowchartEntrance } from './flowchart-camera';
import type { FlowchartEdgeData, FlowchartNodeData } from './flowchart-types';
import type { LayoutResult } from './layout-flowchart';

const FIT_VIEW_DURATION = 460;
const FIT_VIEW_PADDING = 0.32;
const FOCUS_NODE_PADDING = 0.85;
const FOCUS_NODE_MAX_ZOOM = 1.25;
const MIN_REFIT_SIZE = 4;

interface UseFlowchartViewportInput {
  focusedNodeId: string | null;
  layout: LayoutResult | null;
  reactFlowInstance: ReactFlowInstance<Node<FlowchartNodeData>, Edge<FlowchartEdgeData>> | null;
  setFocusedNodeId: Dispatch<SetStateAction<string | null>>;
}

interface UseFlowchartViewportResult {
  centerCameraOnNode: (nodeId: string) => void;
  hasContainerSize: boolean;
  reactFlowWrapperRef: RefObject<HTMLDivElement | null>;
}

function useLatestFocusedNodeRef(
  focusedNodeId: string | null,
): MutableRefObject<string | null> {
  const focusedNodeIdRef = useRef<string | null>(null);
  useEffect(() => {
    focusedNodeIdRef.current = focusedNodeId;
  }, [focusedNodeId]);
  return focusedNodeIdRef;
}

function useMeasuredFlowchartContainer(
  reactFlowWrapperRef: RefObject<HTMLDivElement | null>,
): boolean {
  const [hasContainerSize, setHasContainerSize] = useState<boolean>(false);

  useEffect(() => {
    const wrapper = reactFlowWrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const { width, height } = entry.contentRect;
      setHasContainerSize(width > MIN_REFIT_SIZE && height > MIN_REFIT_SIZE);
    });

    observer.observe(wrapper);
    return () => {
      observer.disconnect();
    };
  }, [reactFlowWrapperRef]);

  return hasContainerSize;
}

export function useFlowchartViewport({
  focusedNodeId,
  layout,
  reactFlowInstance,
  setFocusedNodeId,
}: UseFlowchartViewportInput): UseFlowchartViewportResult {
  const reactFlowWrapperRef = useRef<HTMLDivElement | null>(null);
  const focusedNodeIdRef = useLatestFocusedNodeRef(focusedNodeId);
  const appearedContentKeyRef = useRef<string | null>(null);
  const hasContainerSize = useMeasuredFlowchartContainer(reactFlowWrapperRef);
  const contentKey = layout?.contentKey ?? null;

  const centerCameraOnNode = useCallback((nodeId: string) => {
    if (!reactFlowInstance) {
      return;
    }
    reactFlowInstance.fitView({
      nodes: [{ id: nodeId }],
      duration: FIT_VIEW_DURATION,
      padding: FOCUS_NODE_PADDING,
      maxZoom: FOCUS_NODE_MAX_ZOOM,
    });
  }, [reactFlowInstance]);

  useEffect(() => {
    const entrance = getFlowchartEntrance({
      appearedContentKey: appearedContentKeyRef.current,
      contentKey,
      hasContainerSize,
      layout,
      reactFlowInstance,
    });
    if (!entrance) {
      return;
    }

    appearedContentKeyRef.current = entrance.contentKey;
    return scheduleFlowchartEntrance({
      centerCameraOnNode,
      focusedNodeIdRef,
      layout: entrance.layout,
      reactFlowInstance: entrance.reactFlowInstance,
      setFocusedNodeId,
    });
  }, [
    centerCameraOnNode,
    contentKey,
    focusedNodeIdRef,
    hasContainerSize,
    layout,
    reactFlowInstance,
    setFocusedNodeId,
  ]);

  useEffect(() => {
    const wrapper = reactFlowWrapperRef.current;
    if (!wrapper || !reactFlowInstance || typeof ResizeObserver === 'undefined') {
      return;
    }

    let animationFrame = 0;
    const reframeAfterResize = (): void => {
      if (appearedContentKeyRef.current == null) {
        return;
      }
      const focusTarget = focusedNodeIdRef.current;
      if (focusTarget) {
        centerCameraOnNode(focusTarget);
        return;
      }
      reactFlowInstance.fitView({ duration: FIT_VIEW_DURATION, padding: FIT_VIEW_PADDING });
    };
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const { width, height } = entry.contentRect;
      if (width <= MIN_REFIT_SIZE || height <= MIN_REFIT_SIZE) {
        return;
      }
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(reframeAfterResize);
    });

    observer.observe(wrapper);
    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [centerCameraOnNode, focusedNodeIdRef, reactFlowInstance, reactFlowWrapperRef]);

  return {
    centerCameraOnNode,
    hasContainerSize,
    reactFlowWrapperRef,
  };
}
